/**
 * Settings → Invoicing (design L), the pure half: the editable form model,
 * validation that mirrors the server (lambda/invoices/numbering.mjs and
 * settings.mjs) so most mistakes are caught before a round trip, and the
 * per-tab PUT patch. The server merges a PUT at the top-level key, so each
 * tab sends only its own keys, and only the ones that changed: saving Tax
 * can never overwrite Branding.
 */
import type { InvoiceSettings, PaymentTerms } from '../../internals/repository/InvoiceRepository';
import { computeTotals } from './math/invoiceMath';
import { addDays } from './format';
import { contrastOnWhite, safeAccent } from './components/InvoicePreview';
import type { PreviewDoc } from './components/InvoicePreview';

export type SettingsTab = 'branding' | 'numbering' | 'tax' | 'terms' | 'payments' | 'templates';
export const TABS: { key: SettingsTab; label: string }[] = [
  { key: 'branding', label: 'Branding' },
  { key: 'numbering', label: 'Numbering' },
  { key: 'tax', label: 'Tax' },
  { key: 'terms', label: 'Payment terms' },
  { key: 'payments', label: 'Payment options' },
  { key: 'templates', label: 'Message templates' },
];

/* ── Numbering ───────────────────────────────────────────────────────── */

export type SeriesKey = 'INVOICE' | 'QUOTATION' | 'CREDIT_NOTE' | 'RECEIPT' | 'TAX_INVOICE';
export const SERIES: { key: SeriesKey; label: string }[] = [
  { key: 'INVOICE', label: 'Invoice' },
  { key: 'QUOTATION', label: 'Quotation' },
  { key: 'CREDIT_NOTE', label: 'Credit note' },
  { key: 'RECEIPT', label: 'Receipt' },
];
export const DEFAULT_SERIES: Record<Exclude<SeriesKey, 'TAX_INVOICE'>, string> = {
  INVOICE: 'INV-{YYYY}-{SEQ:5}',
  QUOTATION: 'QUO-{YYYY}-{SEQ:5}',
  CREDIT_NOTE: 'CN-{YYYY}-{SEQ:5}',
  RECEIPT: 'RCP-{YYYY}-{SEQ:5}',
};
export const TAX_PRESET = '{YY}{MMM}_{BRANCH}_{SEQ:5}';
export const MAX_NUMBER_LENGTH = 40;
export const TOKENS: { token: string; desc: string }[] = [
  { token: '{YYYY}', desc: 'Year, 4 digits' },
  { token: '{YY}', desc: 'Year, 2 digits' },
  { token: '{MM}', desc: 'Month, 2 digits' },
  { token: '{MMM}', desc: 'Month, 3 letters (OCT)' },
  { token: '{#####}', desc: 'Running number, zero padded' },
  { token: '{BR}', desc: 'Branch code' },
];

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** The design's aliases, as the server reads them: {#####} → {SEQ:5}, {BR} → {BRANCH}. */
export const normaliseFormat = (format: string) => String(format || '')
  .replace(/\{(#+)\}/g, (_, h: string) => `{SEQ:${h.length}}`)
  .replace(/\{BR\}/g, '{BRANCH}');

/** The number a format gives on `date` (YYYY-MM-DD) for sequence `next`. */
export function expandExample(format: string, date: string, branchCode: string | undefined, next: number | string): string {
  const [y, m] = date.split('-');
  return normaliseFormat(format)
    .replace(/\{YYYY\}/g, y)
    .replace(/\{YY\}/g, y.slice(2))
    .replace(/\{MMM\}/g, MONTHS[Number(m) - 1])
    .replace(/\{MM\}/g, m)
    .replace(/\{BRANCH\}/g, branchCode || '')
    .replace(/\{SEQ:(\d+)\}/g, (_, n: string) => String(next).padStart(Number(n), '0'));
}

export type FormatProblem = 'required' | 'spaces' | 'no_sequence' | 'two_sequences' | 'bad_width' | 'unknown_token' | 'too_long';

/** Same rules as the server's formatProblem, but says which rule failed. */
export function formatProblem(format: string, branchCode?: string): FormatProblem | null {
  const f = normaliseFormat(format);
  if (!f) return 'required';
  if (/\s/.test(f)) return 'spaces';
  const seqs = f.match(/\{SEQ:(\d+)\}/g) || [];
  if (seqs.length === 0) return 'no_sequence';
  if (seqs.length > 1) return 'two_sequences';
  const width = Number((seqs[0] as string).match(/\d+/)![0]);
  if (width < 1 || width > 12) return 'bad_width';
  if (/[{}]/.test(f.replace(/\{(YYYY|YY|MMM|MM|BRANCH|SEQ:\d+)\}/g, ''))) return 'unknown_token';
  const longest = expandExample(f, '2026-12-31', branchCode || 'XXXX', '9'.repeat(width));
  if (longest.length > MAX_NUMBER_LENGTH) return 'too_long';
  return null;
}

export const FORMAT_MESSAGES: Record<FormatProblem, string> = {
  required: 'Enter a format.',
  spaces: 'Remove the spaces. Use - or _ instead.',
  no_sequence: 'Add a running number, for example {#####}.',
  two_sequences: 'Use the running number only once.',
  bad_width: 'The running number can be 1 to 12 digits.',
  unknown_token: 'Use only {YYYY} {YY} {MM} {MMM} {#####} {BR}.',
  too_long: `Numbers can be at most ${MAX_NUMBER_LENGTH} characters.`,
};

/** "Next number" may only go up from what the server reports. */
export function nextNumberProblem(text: string, serverNext?: number): string | null {
  if (text === '' || text == null) return null;
  if (!/^\d+$/.test(text) || Number(text) < 1) return 'Enter a whole number, 1 or more.';
  if (serverNext && Number(text) < serverNext) return `It can only be raised. The next number is ${serverNext}.`;
  return null;
}

/* ── Tax ─────────────────────────────────────────────────────────────── */

/** Sri Lanka TIN: 9 digits, optionally a -NNNN suffix (VAT, e.g. -7000). Empty is allowed. */
export const tinValid = (tin: string) => !tin || /^\d{9}(-\d{4})?$/.test(tin.trim());

/** "18" or "12.5" → basis points; NaN when it is not a rate. */
export function percentToBp(text: string): number {
  const t = String(text ?? '').trim().replace(/%$/, '');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return NaN;
  const [whole, frac = ''] = t.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}
export const bpToPercent = (bp: number) => {
  const s = `${Math.floor(bp / 100)}.${String(bp % 100).padStart(2, '0')}`;
  return s.replace(/\.?0+$/, '') || '0';
};

export interface TaxRateRow { code: string; label: string; rate: string; isDefault: boolean }

/** Field errors keyed like the server's (`taxRates[1].code`, `taxRates`). */
export function taxRateProblems(rows: TaxRateRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  const seen = new Set<string>();
  rows.forEach((r, i) => {
    const code = r.code.trim();
    if (!code) out[`taxRates[${i}].code`] = 'Enter a code.';
    else if (!/^[A-Za-z0-9_-]{1,20}$/.test(code)) out[`taxRates[${i}].code`] = 'Letters, digits, - and _ only.';
    else if (seen.has(code.toUpperCase())) out[`taxRates[${i}].code`] = 'Codes must be different.';
    seen.add(code.toUpperCase());
    if (!r.label.trim()) out[`taxRates[${i}].label`] = 'Enter a label.';
    const bp = percentToBp(r.rate);
    if (Number.isNaN(bp) || bp > 10000) out[`taxRates[${i}].rateBp`] = 'Enter 0 to 100.';
  });
  if (rows.length && rows.filter((r) => r.isDefault).length !== 1) out.taxRates = 'Choose one default rate.';
  return out;
}

/* ── Branding ────────────────────────────────────────────────────────── */

export const ACCENTS = ['#4E45E4', '#0E9F6E', '#C2740B', '#0C8FD4', '#D4602F', '#131318', '#F2C94C'];
export const isHex = (s: string) => /^#[0-9A-Fa-f]{6}$/.test(s);

export function accentCheck(hex: string): { valid: boolean; ratio: number; passes: boolean; used: string; message: string } {
  if (!isHex(hex)) return { valid: false, ratio: 0, passes: false, used: '#4E45E4', message: 'Enter a colour as #RRGGBB.' };
  const ratio = contrastOnWhite(hex);
  const passes = ratio >= 4.5;
  const r = (Math.floor(ratio * 10) / 10).toFixed(1);
  return {
    valid: true, ratio, passes, used: safeAccent(hex),
    message: passes
      ? `Passes WCAG AA on white (${r}:1).`
      : 'Below 4.5:1 on white — the preview uses #4E45E4 instead.',
  };
}

/* ── The form ────────────────────────────────────────────────────────── */

export interface BankRow { bankName: string; branch: string; accountName: string; accountNumber: string }
export interface SeriesRow { format: string; next: string }

export interface SettingsForm {
  accentColor: string;
  footerText: string;
  series: Record<SeriesKey, SeriesRow>;
  taxRates: TaxRateRow[];
  taxInvoiceEnabled: boolean;
  taxInvoiceMode: 'ASK' | 'ALWAYS_FOR_VAT_CUSTOMERS' | 'NEVER';
  shopTin: string;
  vatNumber: string;
  branchCode: string;
  roundWhole: boolean;
  terms: PaymentTerms;
  customDays: string;
  quotationValidDays: string;
  defaultNotes: string;
  defaultTerms: string;
  bankTransfer: boolean;
  lankaQr: boolean;
  card: boolean;
  payAtShop: boolean;
  requireProof: boolean;
  bankAccounts: BankRow[];
  /** The shop's template overrides, every known key present (see canonicalTemplates). */
  templates: Templates;
}

const nextText = (n?: number) => (n ? String(n) : '');

export function toForm(s: InvoiceSettings): SettingsForm {
  const series = (k: SeriesKey): SeriesRow => {
    const x = s.series?.[k];
    return { format: x?.format ?? (k === 'TAX_INVOICE' ? '' : DEFAULT_SERIES[k]), next: nextText(x?.nextNumber) };
  };
  return {
    accentColor: s.branding?.accentColor || '#4E45E4',
    footerText: s.branding?.footerText ?? '',
    series: {
      INVOICE: series('INVOICE'), QUOTATION: series('QUOTATION'), CREDIT_NOTE: series('CREDIT_NOTE'),
      RECEIPT: series('RECEIPT'), TAX_INVOICE: series('TAX_INVOICE'),
    },
    taxRates: (s.taxRates ?? []).map((r) => ({ code: r.code, label: r.label, rate: bpToPercent(r.rateBp), isDefault: !!r.isDefault })),
    taxInvoiceEnabled: !!s.taxInvoiceEnabled,
    taxInvoiceMode: s.taxInvoiceMode ?? 'ASK',
    shopTin: s.shopTin ?? '',
    vatNumber: s.vatNumber ?? '',
    branchCode: s.branchCode ?? '',
    roundWhole: !!s.roundTaxInvoiceToWholeRupee,
    terms: s.defaultPaymentTerms ?? 'NET_15',
    customDays: s.defaultCustomTermDays ? String(s.defaultCustomTermDays) : '',
    quotationValidDays: String(s.quotationValidDays ?? 14),
    defaultNotes: s.defaultNotes ?? '',
    defaultTerms: s.defaultTerms ?? '',
    bankTransfer: !!s.paymentOptions?.bankTransfer,
    lankaQr: !!s.paymentOptions?.lankaQr,
    card: !!s.paymentOptions?.card,
    payAtShop: !!s.paymentOptions?.payAtShop,
    requireProof: !!s.requireProofForTransfers,
    bankAccounts: (s.bankAccounts ?? []).map((b) => ({ bankName: b.bankName, branch: b.branch ?? '', accountName: b.accountName, accountNumber: b.accountNumber })),
    templates: canonicalTemplates((s as any).templates),
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const intOr = (text: string, fallback: number) => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : fallback);

/** Each tab's values as the server stores them. */
function tabValues(tab: SettingsTab, f: SettingsForm): Record<string, unknown> {
  switch (tab) {
    case 'branding':
      return { branding: { accentColor: f.accentColor.toUpperCase(), footerText: f.footerText } };
    case 'tax':
      return {
        taxRates: f.taxRates.map((r) => ({ code: r.code.trim(), label: r.label.trim(), rateBp: percentToBp(r.rate), isDefault: r.isDefault })),
        taxInvoiceEnabled: f.taxInvoiceEnabled,
        taxInvoiceMode: f.taxInvoiceMode,
        shopTin: f.shopTin.trim(),
        vatNumber: f.vatNumber.trim(),
        branchCode: f.branchCode.trim(),
        roundTaxInvoiceToWholeRupee: f.roundWhole,
      };
    case 'terms':
      return {
        defaultPaymentTerms: f.terms,
        ...(f.terms === 'CUSTOM' ? { defaultCustomTermDays: intOr(f.customDays, 0) } : {}),
        quotationValidDays: intOr(f.quotationValidDays, 14),
        defaultNotes: f.defaultNotes,
        defaultTerms: f.defaultTerms,
      };
    case 'payments':
      return {
        paymentOptions: { bankTransfer: f.bankTransfer, lankaQr: f.lankaQr, card: f.card, payAtShop: f.payAtShop },
        bankAccounts: f.bankAccounts.map((b) => ({
          bankName: b.bankName.trim(), ...(b.branch.trim() ? { branch: b.branch.trim() } : {}),
          accountName: b.accountName.trim(), accountNumber: b.accountNumber.trim(),
        })),
        requireProofForTransfers: f.requireProof,
      };
    case 'templates':
      return { templates: canonicalTemplates(f.templates) };
    default:
      return {};
  }
}

function savedValues(tab: SettingsTab, s: InvoiceSettings): Record<string, unknown> {
  return tabValues(tab, toForm(s));
}

/**
 * The PUT body for one tab: its top-level keys whose value changed. For
 * Numbering, each changed series carries its format, plus nextNumber only
 * when the user typed a different one.
 */
export function buildPatch(tab: SettingsTab, form: SettingsForm, saved: InvoiceSettings): Record<string, unknown> {
  if (tab === 'numbering') {
    const before = toForm(saved).series;
    const series: Record<string, { format: string; nextNumber?: number }> = {};
    (Object.keys(form.series) as SeriesKey[]).forEach((k) => {
      const now = form.series[k];
      const was = before[k];
      if (k === 'TAX_INVOICE' && !now.format.trim() && !was.format) return;
      const formatChanged = now.format.trim() !== was.format;
      const nextChanged = now.next.trim() !== '' && now.next.trim() !== was.next;
      if (!formatChanged && !nextChanged) return;
      series[k] = { format: now.format.trim(), ...(nextChanged ? { nextNumber: Number(now.next.trim()) } : {}) };
    });
    return Object.keys(series).length ? { series } : {};
  }
  const now = tabValues(tab, form);
  const was = savedValues(tab, saved);
  const patch: Record<string, unknown> = {};
  Object.keys(now).forEach((k) => { if (!same(now[k], was[k])) patch[k] = now[k]; });
  return patch;
}

export const isDirty = (tab: SettingsTab, form: SettingsForm, saved: InvoiceSettings) =>
  Object.keys(buildPatch(tab, form, saved)).length > 0;

/** Client-side problems for one tab, keyed by the server's field paths. */
export function validateTab(tab: SettingsTab, f: SettingsForm, saved: InvoiceSettings): Record<string, string> {
  const out: Record<string, string> = {};
  if (tab === 'branding') {
    if (!isHex(f.accentColor)) out['branding.accentColor'] = 'Enter a colour as #RRGGBB.';
    if (f.footerText.length > 200) out['branding.footerText'] = 'Keep it under 200 characters.';
  }
  if (tab === 'numbering') {
    (Object.keys(f.series) as SeriesKey[]).forEach((k) => {
      const row = f.series[k];
      const serverNext = saved.series?.[k]?.nextNumber;
      if (k === 'TAX_INVOICE' && !row.format.trim()) {
        if (saved.series?.TAX_INVOICE?.format) out['series.TAX_INVOICE.format'] = 'A tax invoice series cannot be removed once set.';
        return;
      }
      const p = formatProblem(row.format.trim(), f.branchCode.trim() || undefined);
      if (p) out[`series.${k}.format`] = FORMAT_MESSAGES[p];
      const n = nextNumberProblem(row.next.trim(), serverNext);
      if (n) out[`series.${k}.nextNumber`] = n;
    });
  }
  if (tab === 'tax') {
    Object.assign(out, taxRateProblems(f.taxRates));
    if (!tinValid(f.shopTin)) out.shopTin = 'A TIN is 9 digits, optionally followed by -NNNN.';
    if (f.branchCode.trim() && !/^[A-Za-z0-9-]{1,10}$/.test(f.branchCode.trim())) out.branchCode = 'Up to 10 letters or digits.';
    if (f.taxInvoiceEnabled && !f.shopTin.trim()) out.shopTin = 'Tax invoices need the shop TIN.';
  }
  if (tab === 'terms') {
    const d = f.customDays.trim();
    if (f.terms === 'CUSTOM' && (!/^\d+$/.test(d) || Number(d) < 1 || Number(d) > 365)) out.defaultCustomTermDays = 'Enter 1 to 365 days.';
    const q = f.quotationValidDays.trim();
    if (!/^\d+$/.test(q) || Number(q) < 1 || Number(q) > 365) out.quotationValidDays = 'Enter 1 to 365 days.';
  }
  if (tab === 'payments') {
    if (f.bankTransfer && !f.bankAccounts.length) out.bankAccounts = 'Add at least one bank account.';
    f.bankAccounts.forEach((b, i) => {
      if (!b.bankName.trim()) out[`bankAccounts[${i}].bankName`] = 'Enter the bank.';
      if (!b.accountName.trim()) out[`bankAccounts[${i}].accountName`] = 'Enter the account name.';
      if (!b.accountNumber.trim()) out[`bankAccounts[${i}].accountNumber`] = 'Enter the account number.';
      else if (!/^[0-9 -]{4,30}$/.test(b.accountNumber.trim())) out[`bankAccounts[${i}].accountNumber`] = 'Digits only.';
    });
  }
  if (tab === 'templates') Object.assign(out, templateProblems(f.templates));
  return out;
}

/** Server field codes → the copy shown under the field. */
export function serverFieldMessage(path: string, code: string): string {
  if (path.startsWith('templates.')) {
    if (code === 'link_required') return 'Add {link} so the customer can open the document.';
    if (code === 'unknown') return 'Not a template this version understands.';
    return `Enter a message of up to ${MAX_TEMPLATE_LENGTH} characters.`;
  }
  if (code === 'cannot_lower') return 'It can only be raised, never lowered.';
  if (code === 'invalid_series_format') return 'This format is not valid. No spaces, one running number, at most 40 characters.';
  if (path === 'shopTin') return 'A TIN is 9 digits, optionally followed by -NNNN.';
  if (path === 'taxRates' && code === 'one_default_required') return 'Choose one default rate.';
  if (path === 'taxRates') return 'Check the tax rates.';
  if (path === 'bankAccounts') return 'Each account needs a bank, an account name and a number.';
  if (code === 'unknown') return 'Not a setting this version understands.';
  return 'Check this value.';
}

/** After a save, keep unsaved edits on the other tabs. */
export function mergeAfterSave(tab: SettingsTab, form: SettingsForm, result: InvoiceSettings): SettingsForm {
  const fresh = toForm(result);
  const keys: Record<SettingsTab, (keyof SettingsForm)[]> = {
    branding: ['accentColor', 'footerText'],
    numbering: ['series'],
    tax: ['taxRates', 'taxInvoiceEnabled', 'taxInvoiceMode', 'shopTin', 'vatNumber', 'branchCode', 'roundWhole'],
    terms: ['terms', 'customDays', 'quotationValidDays', 'defaultNotes', 'defaultTerms'],
    payments: ['bankTransfer', 'lankaQr', 'card', 'payAtShop', 'requireProof', 'bankAccounts'],
    templates: ['templates'],
  };
  const next = { ...form } as any;
  keys[tab].forEach((k) => { next[k] = fresh[k]; });
  return next;
}

/* ── Branding preview ────────────────────────────────────────────────── */

/** A sample document built from the shop, for the Branding preview (no network). */
export function sampleDoc(f: SettingsForm, today: string, shop?: any): PreviewDoc {
  const rate = f.taxRates.find((r) => r.isDefault);
  const rateBp = rate ? percentToBp(rate.rate) : 0;
  const bp = Number.isNaN(rateBp) ? 0 : rateBp;
  const lines = [
    { description: 'Lunch buffet for 40 guests', quantity: 40, unit: 'head', unitPriceMinor: 125000, taxRateBp: bp },
    { description: 'Delivery and setup', quantity: 1, unitPriceMinor: 350000, taxRateBp: bp },
  ];
  const t = computeTotals({ lines });
  const next = f.series.INVOICE.next || '1';
  const invFormat = formatProblem(f.series.INVOICE.format) ? DEFAULT_SERIES.INVOICE : f.series.INVOICE.format;
  return {
    docType: 'INVOICE',
    number: expandExample(invFormat, today, f.branchCode, next),
    status: 'ISSUED',
    customer: { name: 'Nimal Perera', companyName: 'Sample customer', address: 'Colombo' },
    seller: null,
    issueDate: today,
    dueDate: addDays(today, 15),
    paymentTerms: 'NET_15',
    lines: lines.map((l, i) => ({ ...l, lineTotalMinor: t.lineTotalsMinor[i], discountMinor: t.lineDiscountsMinor[i] })),
    subtotalMinor: t.subtotalMinor,
    discountMinor: t.discountMinor,
    chargesMinor: t.chargesMinor,
    taxLines: t.taxLines.map((x) => ({ ...x, label: rate?.label })),
    roundingMinor: t.roundingMinor,
    totalMinor: t.totalMinor,
    balanceMinor: t.balanceMinor,
    amountInWords: t.amountInWords,
    notes: f.defaultNotes || undefined,
    terms: f.defaultTerms || undefined,
    currency: shop?.currency && shop.currency.length === 3 ? shop.currency : 'LKR',
  };
}

/* ── Message templates ───────────────────────────────────────────────── */
/*
 * Mirrors lambda/invoices/templates.mjs. The PUT merges `templates` one level
 * deep (each template key replaces its whole object), so the patch always
 * carries every known key, an empty {} where the shop has no overrides left:
 * leaving a key out would keep its old overrides on the server.
 */

export type TemplateKey = 'invoice_issued' | 'invoice_reminder' | 'invoice_overdue' | 'payment_received' | 'quotation_sent';
export type TemplateChannel = 'WHATSAPP' | 'SMS' | 'EMAIL';
export type TemplateLang = 'en' | 'si' | 'ta';
export type EmailTemplate = { subject: string; text: string };
export type TemplateValue = string | EmailTemplate;
export type Templates = Record<string, Partial<Record<TemplateChannel, Partial<Record<TemplateLang, TemplateValue>>>>>;
export type TemplateDefaults = Record<string, Record<string, Record<string, TemplateValue>>>;

export const TEMPLATE_KEYS: { key: TemplateKey; label: string }[] = [
  { key: 'invoice_issued', label: 'Invoice issued' },
  { key: 'invoice_reminder', label: 'Payment reminder' },
  { key: 'invoice_overdue', label: 'Overdue notice' },
  { key: 'payment_received', label: 'Payment received' },
  { key: 'quotation_sent', label: 'Quotation' },
];
export const TEMPLATE_CHANNELS: { key: TemplateChannel; label: string }[] = [
  { key: 'WHATSAPP', label: 'WhatsApp' },
  { key: 'SMS', label: 'SMS' },
  { key: 'EMAIL', label: 'Email' },
];
export const TEMPLATE_LANGS: { key: TemplateLang; label: string }[] = [
  { key: 'en', label: 'English' },
  { key: 'si', label: 'සිංහල' },
  { key: 'ta', label: 'தமிழ்' },
];
export const MAX_TEMPLATE_LENGTH = 1000;

/** Chips show the design's snake_case names and insert the camelCase ones. */
export const TEMPLATE_CHIPS: { label: string; insert: string }[] = [
  { label: '{customer_first_name}', insert: '{customerFirstName}' },
  { label: '{invoice_number}', insert: '{number}' },
  { label: '{amount}', insert: '{amount}' },
  { label: '{balance}', insert: '{balance}' },
  { label: '{due_date}', insert: '{dueDate}' },
  { label: '{shop_name}', insert: '{shopName}' },
  { label: '{link}', insert: '{link}' },
];

const TEMPLATE_VARS = ['customerName', 'customerFirstName', 'shopName', 'documentType', 'number', 'amount', 'balance', 'dueDate', 'validUntil', 'link', 'shopPhone'];
const TEMPLATE_ALIASES: Record<string, string> = {
  customer_name: 'customerName', customer_first_name: 'customerFirstName', shop_name: 'shopName',
  invoice_number: 'number', document_number: 'number', due_date: 'dueDate', valid_until: 'validUntil',
  shop_phone: 'shopPhone', document_type: 'documentType',
};

export const templatePath = (key: string, ch: TemplateChannel, lang: TemplateLang) => `templates.${key}.${ch}.${lang}`;

const textOf = (v?: TemplateValue) => (typeof v === 'string' ? v : v?.text);
const present = (v?: TemplateValue) => {
  const t = textOf(v);
  return typeof t === 'string' && t.trim() !== '';
};

/** Every known key (empty {} when none), channels and languages in a fixed order. */
export function canonicalTemplates(t?: unknown): Templates {
  const src = (t && typeof t === 'object' ? t : {}) as Templates;
  const out: Templates = {};
  TEMPLATE_KEYS.forEach(({ key }) => {
    const byCh: Templates[string] = {};
    TEMPLATE_CHANNELS.forEach(({ key: ch }) => {
      const byLang: Partial<Record<TemplateLang, TemplateValue>> = {};
      TEMPLATE_LANGS.forEach(({ key: lang }) => {
        const v = src[key]?.[ch]?.[lang];
        if (typeof v === 'string') byLang[lang] = v;
        else if (v && typeof v === 'object') byLang[lang] = { subject: String(v.subject ?? ''), text: String(v.text ?? '') };
      });
      if (Object.keys(byLang).length) byCh[ch] = byLang;
    });
    out[key] = byCh;
  });
  return out;
}

export type TemplateSource = 'override' | 'default' | 'whatsapp' | 'english';
export interface ResolvedTemplate { text: string; subject?: string; source: TemplateSource }

/**
 * What the server would send for key × channel × language, in its order: the
 * shop's override, the built-in text, then the WhatsApp text in that language
 * (Sinhala and Tamil SMS have no short version), then English.
 */
export function resolveTemplate(templates: Templates, defaults: TemplateDefaults | undefined, key: string, ch: TemplateChannel, lang: TemplateLang): ResolvedTemplate {
  const shop = templates[key] || {};
  const def = defaults?.[key] || {};
  const candidates: [TemplateValue | undefined, TemplateSource][] = [
    [shop[ch]?.[lang], 'override'],
    [def[ch]?.[lang], 'default'],
    [shop.WHATSAPP?.[lang], 'whatsapp'],
    [def.WHATSAPP?.[lang], 'whatsapp'],
    [shop[ch]?.en, 'english'],
    [def[ch]?.en, 'english'],
    [def.WHATSAPP?.en, 'english'],
  ];
  // An override is shown even while empty, so the editor can hold what was typed.
  const own = shop[ch]?.[lang];
  const hit = own !== undefined ? candidates[0] : candidates.find(([v]) => present(v));
  const v = hit?.[0];
  let source: TemplateSource = hit?.[1] ?? 'default';
  if (ch === 'WHATSAPP' && source === 'whatsapp') source = 'default';
  const text = textOf(v) ?? '';
  if (ch !== 'EMAIL') return { text, source };
  const subjectOf = (x?: TemplateValue) => (x && typeof x === 'object' ? x.subject : undefined);
  const subject = subjectOf(v) ?? subjectOf(shop.EMAIL?.en) ?? subjectOf(def.EMAIL?.en) ?? '';
  return { text, subject, source };
}

/** The text and subject the shop would get with no override for this slot. */
export function defaultFor(templates: Templates, defaults: TemplateDefaults | undefined, key: string, ch: TemplateChannel, lang: TemplateLang): ResolvedTemplate {
  return resolveTemplate(removeTemplate(templates, key, ch, lang), defaults, key, ch, lang);
}

/** Set one override; typing the default text back removes it. */
export function setTemplate(
  templates: Templates, defaults: TemplateDefaults | undefined, key: string, ch: TemplateChannel, lang: TemplateLang,
  next: { text: string; subject?: string },
): Templates {
  const base = defaultFor(templates, defaults, key, ch, lang);
  if (next.text === base.text && (ch !== 'EMAIL' || (next.subject ?? '') === (base.subject ?? ''))) {
    return removeTemplate(templates, key, ch, lang);
  }
  const value: TemplateValue = ch === 'EMAIL' ? { subject: next.subject ?? '', text: next.text } : next.text;
  const byCh = { ...(templates[key] || {}) };
  byCh[ch] = { ...(byCh[ch] || {}), [lang]: value };
  return { ...templates, [key]: byCh };
}

/** "Reset to default": drop that one override, leaving every other one. */
export function removeTemplate(templates: Templates, key: string, ch: TemplateChannel, lang: TemplateLang): Templates {
  if (templates[key]?.[ch]?.[lang] === undefined) return templates;
  const byLang = { ...templates[key][ch] };
  delete byLang[lang];
  const byCh = { ...templates[key] };
  if (Object.keys(byLang).length) byCh[ch] = byLang;
  else delete byCh[ch];
  return { ...templates, [key]: byCh };
}

export const hasOverride = (templates: Templates, key: string, ch: TemplateChannel, lang: TemplateLang) =>
  templates[key]?.[ch]?.[lang] !== undefined;

/** Insert a chip's variable at the caret (or over the selection). */
export function insertAtCursor(value: string, start: number | null | undefined, end: number | null | undefined, token: string): { value: string; caret: number } {
  const s = Math.max(0, Math.min(start ?? value.length, value.length));
  const e = Math.max(s, Math.min(end ?? s, value.length));
  return { value: value.slice(0, s) + token + value.slice(e), caret: s + token.length };
}

/** {names} the renderer does not know: they would reach the customer as typed. */
export function unknownVariables(text: string): string[] {
  const out: string[] = [];
  (String(text || '').match(/\{([^{}\s]*)\}/g) || []).forEach((m) => {
    const name = m.slice(1, -1);
    if (!TEMPLATE_VARS.includes(name) && !TEMPLATE_ALIASES[name] && !out.includes(m)) out.push(m);
  });
  return out;
}

/** Blocking problems for every override, keyed by the server's field paths (subjects add `.subject`). */
export function templateProblems(templates: Templates): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(templates || {}).forEach(([key, byCh]) => {
    Object.entries(byCh || {}).forEach(([ch, byLang]) => {
      Object.entries(byLang || {}).forEach(([lang, v]) => {
        const p = templatePath(key, ch as TemplateChannel, lang as TemplateLang);
        const text = textOf(v as TemplateValue) ?? '';
        if (!text.trim()) out[p] = 'Enter the message.';
        else if (text.length > MAX_TEMPLATE_LENGTH) out[p] = `Keep it to ${MAX_TEMPLATE_LENGTH} characters (now ${text.length}).`;
        else if (ch !== 'EMAIL' && !/\{link\}/.test(text)) out[p] = 'Add {link} so the customer can open the document.';
        if (ch === 'EMAIL' && v && typeof v === 'object') {
          if (!v.subject.trim()) out[`${p}.subject`] = 'Enter a subject.';
          else if (v.subject.length > 200) out[`${p}.subject`] = 'Keep the subject under 200 characters.';
        }
      });
    });
  });
  return out;
}

/** The first problem's slot, so Save can show it. */
export function firstProblemSlot(problems: Record<string, string>): { key: TemplateKey; ch: TemplateChannel; lang: TemplateLang } | null {
  const path = Object.keys(problems).find((p) => p.startsWith('templates.'));
  if (!path) return null;
  const [, key, ch, lang] = path.split('.');
  if (!TEMPLATE_KEYS.some((k) => k.key === key) || !TEMPLATE_CHANNELS.some((c) => c.key === ch) || !TEMPLATE_LANGS.some((l) => l.key === lang)) return null;
  return { key: key as TemplateKey, ch: ch as TemplateChannel, lang: lang as TemplateLang };
}

const sampleMoney = (minor: number, lang: TemplateLang) => {
  const prefix = lang === 'si' ? 'රු.' : lang === 'ta' ? 'ரூ.' : 'Rs.';
  const whole = Math.floor(minor / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${prefix} ${whole}.${String(minor % 100).padStart(2, '0')}`;
};

/** The preview's sample values, formatted like the server's variablesFor. */
export function sampleVariables(shopName: string | undefined, lang: TemplateLang = 'en', shopPhone?: string): Record<string, string> {
  return {
    customerName: 'Nimal Perera',
    customerFirstName: 'Nimal',
    shopName: shopName || 'Your shop',
    shopPhone: shopPhone || '',
    documentType: 'invoice',
    number: 'INV-2026-00042',
    amount: sampleMoney(4850000, lang),
    balance: sampleMoney(4850000, lang),
    dueDate: '31 Oct 2026',
    validUntil: '31 Oct 2026',
    link: 'https://i.netlise.com/Ab3dE9xQ2kLm',
  };
}

/** Fill {variables} (snake_case aliases too); unknown ones stay as typed, like the server. */
export function fillTemplate(text: string, vars: Record<string, string>): string {
  return String(text || '').replace(/\{([a-zA-Z_]+)\}/g, (whole, name: string) => {
    const k = TEMPLATE_ALIASES[name] || name;
    return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : whole;
  });
}

/** SMS encoding, characters and segments: the shared copy of the server's count. */
export { smsSegments } from './shareLinks';
